import { memo, useEffect } from "react";
import { useDispatch } from "react-redux";
import { useSelector } from "store";
import { getInfoNegocio } from "store/slices/negocio";

// material-ui
import { Avatar, Card, CardContent, LinearProgress, linearProgressClasses, List, ListItem, ListItemAvatar, ListItemText, Typography } from "@mui/material";
import { styled, useTheme } from "@mui/material/styles";

// assets
import { IconBuildingStore } from "@tabler/icons";
import { TIENDA_HANDY } from "config";
import useBorderRadius from "hooks/useBorderRadius";

// styles
const BorderLinearProgress = styled(LinearProgress)(({ theme }) => ({
  height: 10,
  borderRadius: 30,
  [`&.${linearProgressClasses.colorPrimary}`]: {
    backgroundColor: theme.palette.mode === "dark" ? theme.palette.dark.light : "#fff",
  },
  [`& .${linearProgressClasses.bar}`]: {
    borderRadius: 5,
    backgroundColor: theme.palette.mode === "dark" ? theme.palette.primary.dark : theme.palette.primary.main,
  },
}));

const CardStyle = styled(Card)(({ theme }) => ({
  background: `linear-gradient(135deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 100%)`,
  marginBottom: "22px",
  overflow: "hidden",
  position: "relative",
  borderRadius: "16px",
  boxShadow: theme.palette.mode === "dark" ? "0 8px 32px rgba(0,0,0,0.3)" : "0 8px 32px rgba(0,0,0,0.1)",
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  cursor: "pointer",
  "&:hover": {
    transform: "translateY(-4px)",
    boxShadow: theme.palette.mode === "dark" ? "0 12px 40px rgba(0,0,0,0.4)" : "0 12px 40px rgba(0,0,0,0.15)",
  },
  "&:before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: "linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%)",
    backdropFilter: "blur(10px)",
    zIndex: 1,
  },
  "&:after": {
    content: '""',
    position: "absolute",
    width: "100px",
    height: "100px",
    background: "rgba(255,255,255,0.1)",
    borderRadius: "50%",
    top: "-30px",
    right: "-30px",
    zIndex: 0,
  },
}));

function MenuCard() {
  const theme = useTheme();
  const dispatch = useDispatch();
  const { info, configAvanzada } = useSelector((state) => state.negocio);

  const urlNegocio = configAvanzada?.url_inicio || info?.negocio_url;

  useEffect(() => {
    if (!info?.negocio_url) {
      dispatch(getInfoNegocio());
    }
  }, [dispatch, info?.negocio_url]);

  const handleCardClick = () => {
    if (urlNegocio) {
      const finalUrl = urlNegocio.startsWith("http://") || urlNegocio.startsWith("https://") ? urlNegocio : `https://${urlNegocio}`;

      window.open(finalUrl, "_blank");
    }
  };

  return (
    <CardStyle sx={{ mt: TIENDA_HANDY ? "20px" : 0, borderRadius: useBorderRadius("16px") }}>
      <CardContent
        sx={{
          p: 3,
          position: "relative",
          zIndex: 2,
          cursor: "pointer",
          transition: "all 0.3s ease",
          "&:hover": {
            "& .MuiAvatar-root": {
              transform: "scale(1.1) rotate(5deg)",
            },
            "& .MuiTypography-subtitle1": {
              color: "#fff",
            },
          },
        }}
        onClick={handleCardClick}
        onKeyPress={(e) => e.key === "Enter" && handleCardClick()}
        role="button"
        tabIndex={0}>
        <List sx={{ p: 0, m: 0 }}>
          <ListItem alignItems="flex-start" disableGutters sx={{ p: 0 }}>
            <ListItemAvatar sx={{ mt: 0 }}>
              <Avatar
                variant="rounded"
                sx={{
                  ...theme.typography.commonAvatar,
                  ...theme.typography.largeAvatar,
                  color: theme.palette.primary.main,
                  border: "none",
                  background: "rgba(255,255,255,0.9)",
                  backdropFilter: "blur(10px)",
                  marginRight: "16px",
                  boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
                  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                  width: 48,
                  height: 48,
                }}>
                <IconBuildingStore fontSize="1.5rem" />
              </Avatar>
            </ListItemAvatar>
            <ListItemText
              sx={{ mt: 0 }}
              primary={
                <Typography
                  variant="subtitle1"
                  sx={{
                    color: "rgba(255,255,255,0.95)",
                    fontWeight: 600,
                    fontSize: "1rem",
                    transition: "color 0.3s ease",
                  }}>
                  Ver mi tienda
                </Typography>
              }
            />
          </ListItem>
        </List>
      </CardContent>
    </CardStyle>
  );
}

export default memo(MenuCard);
